<template>
  <div class="sales-section h-full">
    <div class="section-header flex justify-content-between align-items-center mb-3">
      <h3 class="section-title m-0"><i class="pi pi-chart-pie"></i> Ingreso por Canal</h3>
    </div>

    <!-- POR ESTE BLOQUE LIMPIO: -->
    <div class="total-ingresos-banner mb-3 p-3 text-center border-round bg-green-50" v-if="tieneDatos && !loading">
      <span class="text-xs text-700 font-bold uppercase block tracking-wider">Total Ingresos Brutos</span>
      <span class="text-3xl font-extrabold text-green-700">
        {{ formatearMonto(totales.ingresos_brutos) }} Bs
      </span>
    </div>

    <div class="chart-wrapper flex justify-content-center relative" style="min-height: 250px;">
      <!-- Spinner de carga -->
      <div v-if="loading"
        class="flex align-items-center justify-content-center w-full absolute top-0 left-0 h-full bg-white-alpha-80 z-1">
        <ProgressSpinner style="width:50px;height:50px" />
      </div>

      <!-- Gráfico de Dona -->
      <Chart v-if="tieneDatos" type="doughnut" :data="chartData" :options="chartOptions"
        style="width: 100%; max-width: 400px;" />

      <!-- Mensaje sin datos -->
      <div v-else-if="!loading" class="no-data w-full text-center p-5">
        <i class="pi pi-info-circle text-4xl text-500 mb-3"></i>
        <p class="m-0 text-600">No hay datos de canales para este rango</p>
      </div>
    </div>
  </div>
</template>

<script>
import ReporteService from '@/service/ReporteService';
import Chart from 'primevue/chart';
import ProgressSpinner from 'primevue/progressspinner';

export default {
  name: 'DashboardCanalVenta',
  components: {
    Chart,
    ProgressSpinner
  },
  props: {
    fechaInicio: {
      type: Date,
      required: true
    },
    fechaFin: {
      type: Date,
      required: true
    },
    sucursalId: {
      type: [Number, String],
      default: null
    },
    grupoIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      chartData: {},
      totales: {
        ingresos_brutos: 0,
        total_pedidos: 0,
        ticket_promedio: 0
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const valor = Number(context.raw).toLocaleString('de-DE', { minimumFractionDigits: 2 });
                return ` Monto: ${valor} Bs`;
              }
            }
          }
        }
      }
    };
  },
  computed: {
    tieneDatos() {
      return this.chartData && this.chartData.datasets && this.chartData.datasets.length > 0;
    }
  },
  watch: {
    fechaInicio() { this.cargarDatos(); },
    fechaFin() { this.cargarDatos(); },
    sucursalId() { this.cargarDatos(); },
    grupoIds: {
      handler() { this.cargarDatos(); },
      deep: true
    }
  },
  created() {
    this.reporteService = new ReporteService();
  },
  mounted() {
    this.cargarDatos();
  },
  methods: {
    formatearFechaLocal(date) {
      if (!date) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatearMonto(numero) {
      return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numero || 0);
    },
    async cargarDatos() {
      if (!this.fechaInicio || !this.fechaFin) return;

      this.loading = true;
      try {
        const fechaIniStr = `${this.formatearFechaLocal(this.fechaInicio)} 00:00:00`;
        const fechaFinStr = `${this.formatearFechaLocal(this.fechaFin)} 23:59:59`;

        const params = {
          fecha_inicio: fechaIniStr,
          fecha_fin: fechaFinStr
        };

        if (this.sucursalId) params.sucursal_id = this.sucursalId;
        if (this.grupoIds && this.grupoIds.length > 0) params.grupo_ids = this.grupoIds;

        const response = await this.reporteService.getVentasPorCanal(params);

        if (response && response.labels && response.labels.length > 0) {
          // Guardamos métricas globales
          if (response.totales) {
            this.totales = response.totales;
          }

          // Mapeamos gráfico con etiqueta limpia
          this.chartData = {
            labels: response.labels.map((label, index) => {
              const montoFormatted = this.formatearMonto(response.ventas[index]);
              return `${label} (${montoFormatted} Bs)`;
            }),
            datasets: [
              {
                data: response.ventas,
                backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
                hoverBackgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF']
              }
            ]
          };
        } else {
          this.chartData = {};
          this.totales = { ingresos_brutos: 0, total_pedidos: 0, ticket_promedio: 0 };
        }
      } catch (error) {
        console.error("Error cargando ventas por canal:", error);
        this.chartData = {};
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.sales-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.section-title {
  color: #2c3e50;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  font-weight: bold;
  gap: 0.5rem;
}

.kpi-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #e9ecef;
}

.kpi-title {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 1.1rem;
  font-weight: 700;
  margin-top: 0.25rem;
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.total-ingresos-banner {
  border: 1px solid #c3e6cb;
  background-color: #f4fbf7;
}
</style>